import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Camera, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  hasDashboardContent,
  captureFullDashboard,
  captureVisibleArea,
  captureDashboardContent,
  captureWidgetsOnly,
} from '../utils/screenshotUtil';
import { debugGridLayout, highlightWidgets } from '../utils/debugScreenshot';

const screenshotMessages = (t) => ({
  capturing: t('screenshot.capturing'),
  success: t('screenshot.success'),
  error: t('screenshot.error'),
});

const ScreenshotButton = ({
  showDropdown = true,
  variant = 'full',
  elementSelector,
  filename,
  size = 'icon',
}) => {
  const { t } = useLanguage();
  const location = useLocation();
  const [dashboardLike, setDashboardLike] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDashboardLike(hasDashboardContent());
  }, [location.pathname]);

  const run = useCallback(
    async (fn) => {
      if (busy) return;
      setBusy(true);
      try {
        await fn(screenshotMessages(t));
      } catch {
        /* toasts handled in util */
      } finally {
        setBusy(false);
      }
    },
    [busy, t]
  );

  const onFullPage = () => run((m) => captureFullDashboard(m));
  const onVisible = () => run((m) => captureVisibleArea(m));
  const onDashboard = () => run((m) => captureDashboardContent(m));
  const onWidgetsOnly = () =>
    run(async (m) => {
      if (process.env.NODE_ENV === 'development') {
        debugGridLayout();
        highlightWidgets();
        await new Promise((r) => setTimeout(r, 100));
      }
      await captureWidgetsOnly(m);
    });

  if (!showDropdown) {
    const handleClick = () => {
      if (elementSelector) {
        run(async (m) => {
          const { captureDashboardSection } = await import('../utils/screenshotUtil');
          await captureDashboardSection(
            elementSelector,
            filename || `citius-screenshot-${Date.now()}.png`,
            m
          );
        });
        return;
      }
      if (variant === 'visible') onVisible();
      else if (variant === 'dashboard') onDashboard();
      else if (variant === 'widgets') onWidgetsOnly();
      else onFullPage();
    };
    return (
      <span className='screenshot-ignore inline-flex'>
        <Button
          variant='ghost'
          size={size}
          className='h-8 w-8'
          disabled={busy}
          onClick={handleClick}
          title={t('screenshot.capture')}
          aria-label={t('screenshot.capture')}
        >
          {busy ? <Loader2 className='h-4 w-4 animate-spin' /> : <Camera className='h-4 w-4' />}
        </Button>
      </span>
    );
  }

  return (
    <span className='screenshot-ignore inline-flex'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size={size}
            className='h-8 w-8'
            disabled={busy}
            title={t('screenshot.capture')}
            aria-label={t('screenshot.capture')}
          >
            {busy ? <Loader2 className='h-4 w-4 animate-spin' /> : <Camera className='h-4 w-4' />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='end'
          className='screenshot-ignore screenshot-dropdown w-56'
        >
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              onFullPage();
            }}
          >
            {t('screenshot.fullPage')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              onVisible();
            }}
          >
            {t('screenshot.visibleArea')}
          </DropdownMenuItem>
          {dashboardLike && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  onDashboard();
                }}
              >
                {t('screenshot.dashboard')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  onWidgetsOnly();
                }}
              >
                {t('screenshot.widgetsOnly')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
};

export default ScreenshotButton;
